import {
  type ActiveElement,
  type BarControllerChartOptions,
  type ChartEvent,
  Chart as ChartJS,
  type ChartOptions,
  registerables,
} from "chart.js";
import dayjs from "dayjs";
import { motion } from "motion/react";
import { type MouseEvent, useEffect, useRef, useState } from "react";
import { Bar, getElementAtEvent } from "react-chartjs-2";

import { formatMoneyAmount } from "@krea/lender-features/utils/formatMoneyAmount";
import { Button } from "@krea/shared/design-system/Button";
import { cn } from "@krea/shared/utils";

import { useBankingDataAggregatedTransactions } from "./hooks";
import type {
  IBankAccount,
  IBankingDataResult,
  IBankTransaction,
} from "./types";

ChartJS.register(...registerables);

interface ISelectedDataset {
  x: string;
  label: string;
  transactions: IBankTransaction[];
}

const options: (
  accountsToDisplay: IBankAccount[],
  primaryCurrency: string,
) => ChartOptions = (
  accountsToDisplay: IBankAccount[],
  primaryCurrency: string,
) => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "start",
        labels: {
          boxHeight: 0,
          boxWidth: 17,
          color: "#000",
          padding: 15,
        },
      },
      tooltip: {
        callbacks: {
          afterFooter: (_tooltipItems: any) =>
            "Click to see transaction details",
        },
      },
    },
    elements: {
      bar: {
        borderWidth: 1,
      },
    },
    onHover: (event: ChartEvent, elements: ActiveElement[]) => {
      if (event.native?.target) {
        const target = event.native.target as HTMLElement;
        target.style.cursor = elements[0] ? "pointer" : "default";
      }
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: true,
          drawTicks: false,
          color: "#F0F0F0",
        },
        ticks: {
          padding: 10,
        },
      },
      y: {
        stacked: true,
        grid: {
          display: true,
          drawTicks: false,
          color: "#F0F0F0",
        },
        beginAtZero: true,

        ticks: {
          callback: (value: string | number) => {
            const currency =
              accountsToDisplay.length > 0
                ? (accountsToDisplay[0]?.currency ?? primaryCurrency)
                : primaryCurrency;

            return formatMoneyAmount(Number(value), { digits: 1, currency });
          },
          padding: 10,
        },
      },
    },
  };
};

const transactionsReducer = (
  acc: number,
  transaction: IBankTransaction,
  accountsToDisplay: IBankAccount[],
) => {
  if (
    accountsToDisplay.find(
      (item: IBankAccount) => item.id === transaction.accountId,
    )
  ) {
    return acc + transaction.amount;
  }
  return acc;
};

const formatAmountWithCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

export const BankDataTable = ({
  transactions,
}: {
  transactions: IBankTransaction[];
}) => {
  const thStyle =
    "h-16 px-2 border-t border-gray-300 text-sm align-middle font-medium last:min-w-[120px]";
  const tdStyle = "border-t border-gray-300 p-2";

  return (
    <div className={"max-h-[500px] overflow-x-auto"}>
      <table className="min-w-full border-collapse">
        <thead>
          <tr>
            <th className={cn(thStyle)}>Amount</th>
            <th className={cn(thStyle)}>Text</th>

            <th className={cn(thStyle)}>Counterparty Holder Name</th>

            <th className={cn(thStyle)}>Date</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((item: IBankTransaction, index: number) => (
            <tr key={index} className="bg-white even:bg-gray-100">
              <td className={tdStyle}>
                {formatAmountWithCurrency(Number(item.amount), item.currency)}
              </td>
              <td className={tdStyle}>{item.text}</td>

              <td className={tdStyle}>{item.counterPartyHolderName}</td>

              <td className={tdStyle}>{item.date}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const BankDataAccordion = ({
  transactions,
}: {
  transactions: IBankTransaction[];
}) => {
  return (
    <div className="max-h-[400px] overflow-y-auto">
      {transactions.map((item: IBankTransaction, index: number) => (
        <div key={index} className={cn("bg-white shadow-sm mb-4 p-4")}>
          <div className="flex items-center border-b pb-4 text-gray-500">
            <div>{item.date}</div>
          </div>
          <div className="flex-col pt-4">
            <div className="w-full flex-col md:w-1/2">
              <div className="font-bold">Amount:</div>
              <div className="mb-2">
                {formatAmountWithCurrency(Number(item.amount), item.currency)}
              </div>
            </div>
            <div className="w-full flex-col md:w-1/2">
              <div className="font-bold">Text:</div>
              <div className="mb-2">{item.text}</div>
            </div>
            <div className="w-full flex-col md:w-1/2">
              <div className="font-bold">Counterparty Holder Name:</div>
              <div className="mb-2">{item.counterPartyHolderName}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const BankingDataChart = ({
  resourceId,
  resourceType,
  primaryCurrency,
  isMobile,
}: {
  resourceId: number | string;
  resourceType: "GUARANTOR" | "APPLYING_COMPANY";
  primaryCurrency: string;
  isMobile: boolean;
}) => {
  const chartRef = useRef(null);
  const [accountsToDisplay, setAccountsToDisplay] = useState<IBankAccount[]>(
    [],
  );
  const [selectedDataset, setSelectedDataset] =
    useState<ISelectedDataset | null>(null);

  const { isPending, data: bankingDataResponse } =
    useBankingDataAggregatedTransactions({ resourceId, resourceType });

  useEffect(() => {
    if (
      bankingDataResponse?.accounts &&
      bankingDataResponse.accounts.length > 0
    ) {
      const accounts = bankingDataResponse.accounts;
      setAccountsToDisplay(
        accounts.filter((item) => item.currency === primaryCurrency),
      );
    }
  }, [bankingDataResponse?.accounts, primaryCurrency]);

  // Return early if there is no data to display
  if (
    isPending ||
    (bankingDataResponse?.results && bankingDataResponse.results.length === 0)
  )
    return null;

  const labels: string[] = [];

  const uncategorizedCreditData: any[] = [];
  const uncategorizedDebitData: any[] = [];

  // guarantor categories
  const incomeData: any[] = [];
  const expensesData: any[] = [];
  const paymentsData: any[] = [];
  const gamblingEntertainmentData: any[] = [];
  const loanPaymentData: any[] = [];

  // applying company categories
  const revenueData: any[] = [];

  if (bankingDataResponse && bankingDataResponse.results.length > 0) {
    // startDate and endDate are null only when bankingDataResponse.results is empty
    const { fullMonthsPeriod } = bankingDataResponse;

    const startDate = dayjs(fullMonthsPeriod.startDate);
    const endDate = dayjs(fullMonthsPeriod.endDate);

    bankingDataResponse.results.forEach((entry: IBankingDataResult) => {
      const entryDate = dayjs(entry.date);
      const year = entryDate.format("YYYY-MM");
      let label;

      if (entryDate.isBefore(startDate) || entryDate.isAfter(endDate)) {
        label = `${year} (Not a full month)`;
      } else {
        label = year;
      }

      labels.push(label);

      uncategorizedCreditData.push({
        x: label,
        label: "Uncategorized Credit",
        ...entry.uncategorizedCredit,
        amount: entry.uncategorizedCredit.transactions.reduce(
          (acc: number, transaction: IBankTransaction) => {
            return transactionsReducer(acc, transaction, accountsToDisplay);
          },
          0,
        ),
      });

      uncategorizedDebitData.push({
        x: label,
        label: "Uncategorized Debit",
        ...entry.uncategorizedDebit,
        amount: entry.uncategorizedDebit.transactions.reduce(
          (acc: number, transaction: IBankTransaction) => {
            return transactionsReducer(acc, transaction, accountsToDisplay);
          },
          0,
        ),
      });

      // Guarantor data
      incomeData.push({
        x: label,
        label: "Income",
        ...entry.income,
        amount: entry.income.transactions.reduce(
          (acc: number, transaction: IBankTransaction) => {
            return transactionsReducer(acc, transaction, accountsToDisplay);
          },
          0,
        ),
      });
      expensesData.push({
        x: label,
        label: "Expenses",
        ...entry.expenses,
        amount: entry.expenses.transactions.reduce(
          (acc: number, transaction: IBankTransaction) => {
            return transactionsReducer(acc, transaction, accountsToDisplay);
          },
          0,
        ),
      });
      paymentsData.push({
        x: label,
        label: "Payments",
        ...entry.payments,
        amount: entry.payments.transactions.reduce(
          (acc: number, transaction: IBankTransaction) => {
            return transactionsReducer(acc, transaction, accountsToDisplay);
          },
          0,
        ),
      });
      gamblingEntertainmentData.push({
        x: label,
        label: "Gambling/Entertainment",
        ...entry.gamblingEntertainment,
        amount: entry.gamblingEntertainment.transactions.reduce(
          (acc: number, transaction: IBankTransaction) => {
            return transactionsReducer(acc, transaction, accountsToDisplay);
          },
          0,
        ),
      });
      loanPaymentData.push({
        x: label,
        label: "Loan Payment",
        ...entry.loanPayment,
        amount: entry.loanPayment.transactions.reduce(
          (acc: number, transaction: IBankTransaction) => {
            return transactionsReducer(acc, transaction, accountsToDisplay);
          },
          0,
        ),
      });

      // applying company data

      revenueData.push({
        x: label,
        label: "Revenue",
        ...entry.revenue,
        amount: entry.revenue.transactions.reduce(
          (acc: number, transaction: IBankTransaction) => {
            return transactionsReducer(acc, transaction, accountsToDisplay);
          },
          0,
        ),
      });
    });
  }

  let datasets: {
    label: string;
    data: any;
    borderColor: string;
    backgroundColor: string;
    parsing: { yAxisKey: string };
  }[] = [];

  switch (resourceType) {
    case "GUARANTOR":
      datasets = [
        {
          label: "Uncategorized Credit",
          data: uncategorizedCreditData,
          borderColor: "#93999F",
          backgroundColor: "#93999F",
          parsing: { yAxisKey: "amount" },
        },
        {
          label: "Uncategorized Debit",
          data: uncategorizedDebitData,
          borderColor: "#93999F",
          backgroundColor: "#93999F",
          parsing: { yAxisKey: "amount" },
        },
        {
          label: "Income",
          data: incomeData,
          borderColor: "#1F78B4",
          backgroundColor: "#1F78B4",
          parsing: {
            yAxisKey: "amount",
          },
        },
        {
          label: "Expenses",
          data: expensesData,
          borderColor: "#802392",
          backgroundColor: "#802392",
          parsing: {
            yAxisKey: "amount",
          },
        },

        {
          label: "Payments",
          data: paymentsData,
          borderColor: "#C4C439",
          backgroundColor: "#C4C439",
          parsing: {
            yAxisKey: "amount",
          },
        },
        {
          label: "Gambling/Entertainment",
          data: gamblingEntertainmentData,
          borderColor: "#E75200",
          backgroundColor: "#E75200",
          parsing: {
            yAxisKey: "amount",
          },
        },
        {
          label: "Loan Payment",
          data: loanPaymentData,
          borderColor: "#C11616",
          backgroundColor: "#C11616",
          parsing: {
            yAxisKey: "amount",
          },
        },
      ];
      break;
    case "APPLYING_COMPANY":
      datasets = [
        {
          label: "Uncategorized Credit",
          data: uncategorizedCreditData,
          borderColor: "#93999F",
          backgroundColor: "#93999F",
          parsing: { yAxisKey: "amount" },
        },
        {
          label: "Uncategorized Debit",
          data: uncategorizedDebitData,
          borderColor: "#93999F",
          backgroundColor: "#93999F",
          parsing: { yAxisKey: "amount" },
        },
        {
          label: "Revenue",
          data: revenueData,
          borderColor: "#1F78B4",
          backgroundColor: "#1F78B4",
          parsing: {
            yAxisKey: "amount",
          },
        },
        {
          label: "Loan Payment",
          data: loanPaymentData,
          borderColor: "#C11616",
          backgroundColor: "#C11616",
          parsing: {
            yAxisKey: "amount",
          },
        },
      ];
  }

  const data = {
    labels,
    datasets,
  };

  const onClick = (event: MouseEvent) => {
    const elements = chartRef.current
      ? getElementAtEvent(chartRef.current, event as any)
      : [];

    if (elements.length > 0) {
      const element = elements[0];
      if (!element) return;

      const datasetIndex = element.datasetIndex;
      const index = element.index;
      const dataset = data.datasets[datasetIndex];
      if (!dataset) return;

      const newSelectedDataset = dataset.data[index];

      if (
        selectedDataset &&
        selectedDataset.x === newSelectedDataset.x &&
        selectedDataset.label === newSelectedDataset.label
      ) {
        // If we are currently displaying the selected dataset's detailed transactions, stop displaying it
        setSelectedDataset(null);
      } else {
        // If we are currently not displaying the selected dataset's detailed transactions, display it
        setSelectedDataset(newSelectedDataset);
      }
    }
  };

  const toggleAccountToDisplay = (account: IBankAccount) => {
    if (
      accountsToDisplay.find((item: IBankAccount) => item.id === account.id)
    ) {
      // If we are currently displaying the account, remove it from the list
      setAccountsToDisplay(
        accountsToDisplay.filter(
          (item: IBankAccount) => item.id !== account.id,
        ),
      );
    } else {
      // If we are currently not displaying the account, add it to the list
      // Also only allow accounts of the same currency to be displayed

      const displayAccount = [
        ...accountsToDisplay.filter(
          (it: IBankAccount) => it.currency === account.currency,
        ),
        account,
      ];
      setAccountsToDisplay(displayAccount);
    }
  };

  const renderSelectedDataset = () => {
    return selectedDataset ? (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="border-t"
      >
        <h6 className="ml-3 pt-3 font-bold">
          Transactions - {selectedDataset.label} - {selectedDataset.x}{" "}
          <Button variant="link" onClick={() => setSelectedDataset(null)}>
            Close
          </Button>
        </h6>
        <div>
          {!isMobile ? (
            <BankDataTable transactions={selectedDataset.transactions} />
          ) : (
            <BankDataAccordion transactions={selectedDataset.transactions} />
          )}
        </div>
      </motion.div>
    ) : null;
  };

  return (
    <div className="p-1">
      <div>
        {bankingDataResponse?.accounts.map((account: IBankAccount) => (
          <div key={account.id} className="inline-block pl-3 text-sm font-bold">
            <input
              className="mr-1"
              type="checkbox"
              onChange={() => toggleAccountToDisplay(account)}
              checked={
                !!accountsToDisplay.find(
                  (item: IBankAccount) => item.id === account.id,
                )
              }
            />
            {account.name}{" "}
            <span className="font-normal">({account.currency})</span>
          </div>
        ))}
      </div>
      <div style={{ height: 250 }}>
        <Bar
          ref={chartRef}
          options={
            options(
              accountsToDisplay,
              primaryCurrency,
            ) as BarControllerChartOptions
          }
          data={data}
          onClick={(event: MouseEvent) => onClick(event)}
        />
      </div>
      {renderSelectedDataset()}
    </div>
  );
};
